import { useContext, useEffect, useMemo, useRef, useState } from "react";
import { Dialog } from "primereact/dialog";
import { NavLink, useLocation, useNavigate, useParams } from "react-router-dom";
import {
  empty,
  errorResponse,
  GetPosition,
  isArray,
  isObject,
  isString,
  isUndefined,
  prepareResponseData,
  reIndex,
  toNormalCase,
} from "../../Utilities/utils";
import { AuthContext } from "../Root/ProtectedRoute";
import { toUpper } from "lodash";

// css
import "../students/Students.css";
import "./Assessment.css";

// api
import studentApi from "../../api/Student";
import classApi from "../../api/Classes";
import termApi from "../../api/Terms";
import assessmentApi from "../../api/Assessment";
import sessionApi from "../../api/Session";

//components
import MainHeader from "../../components/headers/mainHeader/MainHeader";
import AppWrapper from "../../components/appWrapper/AppWrapper";
import { Toast } from "primereact/toast";
import FullPageLoader from "../../components/loader/FullPageLoader";
import { Button } from "primereact/button";
import { FaExclamationTriangle, FaPrint } from "react-icons/fa";
import colors from "../../config/colors";
import ButtonIcon from "../../components/buttons/buttonIcon/ButtonIcon";
import Barchart from "../../components/chart/barchart/Barchart";
import ItemLoader from "../../components/loader/ItemLoader";
import EmptyListResponse from "../../components/emptyDiv/EmptyListResponse";
import TitleValue from "../../components/item/TitleValue";

const buildStudentReport = (
  student,
  classData,
  reIndexedSubjects,
  sessionId,
  termId,
) => {
  const studentAssessmentData =
    !empty(student) && !empty(student.assessment) ? student.assessment : [];
  const studentReportsList =
    !empty(student) && !empty(student.reports) ? student.reports : [];
  const classReports =
    !empty(classData) && !empty(classData.reports) ? classData.reports : [];

  let classReport = {};
  for (let i = 0; i < classReports.length; i++) {
    const report = !empty(classReports[i]) ? classReports[i] : {};
    const reportSessionId = !empty(report.sessionId) ? report.sessionId : "";
    const reportTermId = !empty(report.termId) ? report.termId : "";
    if (reportSessionId === sessionId && reportTermId === termId) {
      classReport = report;
      break;
    }
  }

  const selectedSubjectIds =
    !empty(classReport) && !empty(classReport.subjectsToAssess)
      ? classReport.subjectsToAssess
      : [];

  const studentAssessment = studentAssessmentData
    .filter((item) => {
      const itemSessionId =
        !empty(item) && !empty(item.sessionId) ? item.sessionId : "";
      const itemTermId = !empty(item) && !empty(item.termId) ? item.termId : "";
      const itemSubjectId =
        !empty(item) && !empty(item.subjectId) ? item.subjectId : "";
      return (
        itemSessionId === sessionId &&
        itemTermId === termId &&
        selectedSubjectIds.includes(itemSubjectId)
      );
    })
    .map((item) => {
      const subjectId =
        !empty(item) && !empty(item.subjectId) ? item.subjectId : "";
      const subjectInfo = !empty(reIndexedSubjects)
        ? reIndexedSubjects[subjectId]
        : null;
      return {
        ...item,
        subjectCode:
          !empty(subjectInfo) && !empty(subjectInfo.code)
            ? subjectInfo.code
            : "",
        subjectTitle:
          !empty(subjectInfo) && !empty(subjectInfo.title)
            ? subjectInfo.title
            : "",
      };
    });

  let studentReport = {};
  let psychomotor = {};
  let affectiveDomain = {};
  for (let i = 0; i < studentReportsList.length; i++) {
    const entry = studentReportsList[i];
    const itemSessionId =
      !empty(entry) && !empty(entry.sessionId) ? entry.sessionId : "";
    const itemTermId = !empty(entry) && entry.termId ? entry.termId : "";
    if (itemSessionId === sessionId && itemTermId === termId) {
      studentReport = !empty(entry) ? entry : {};
      psychomotor =
        !empty(entry) && !empty(entry.psychomotor) ? entry.psychomotor : {};
      affectiveDomain =
        !empty(entry) && !empty(entry.affectiveDomain)
          ? entry.affectiveDomain
          : {};
      break;
    }
  }

  const activeDetails = { ...student, psychomotor, affectiveDomain };

  const classTeacherName =
    !empty(studentReport) && !empty(studentReport.classTeacherName)
      ? studentReport.classTeacherName
      : !empty(classData) && !empty(classData.classTeacher)
        ? classData.classTeacher
        : "";

  const chartData = {
    labels: studentAssessment.map((data) =>
      !empty(data) && !empty(data.subjectCode) ? data.subjectCode : "",
    ),
    datasets: [
      {
        label: "Subject Performance",
        data: studentAssessment.map((data) =>
          !empty(data) && !empty(data.total) ? data.total : 0,
        ),
        backgroundColor: colors.primary,
        borderRadius: 12,
      },
      {
        label: "Class Average",
        data: studentAssessment.map((data) =>
          !empty(data) && !empty(data.classAverage) ? data.classAverage : 0,
        ),
        backgroundColor: colors.orange,
        borderRadius: 12,
      },
    ],
  };

  const hasPaid = !empty(student) && student.paid === "Yes";

  return {
    student,
    hasPaid,
    studentReport,
    studentAssessment,
    activeDetails,
    classTeacherName,
    chartData,
  };
};

const DisplayAllClassResults = ({ ...props }) => {
  const printRef = useRef();
  const { user } = useContext(AuthContext);
  const location = useLocation();
  const navigate = useNavigate();
  const { termId, sessionId, classId } = useParams();

  //ref
  const toastTR = useRef(null);

  // states
  const [studentsData, setStudentsData] = useState([]);
  const [classData, setClassData] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [isActionLoading, setIsActionLoading] = useState(false);
  const [reIndexedSubjects, setReIndexedSubjects] = useState({});
  const [termData, setTermData] = useState({});
  const [sessionData, setSessionData] = useState({});
  const [ratingIndices, setRatingIndices] = useState([]);
  const [studentToActivate, setStudentToActivate] = useState(null);
  const [activationModalOpen, setActivationModalOpen] = useState(false);

  useEffect(() => {
    if (!termId || !sessionId || !classId) {
      navigate("/404");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (empty(user) || empty(sessionId) || empty(termId) || empty(classId)) {
      return;
    }
    loadClassResults();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?._id]);

  //alert functions
  const responseDialog = (severity = null, summary = null, detail = null) => {
    toastTR?.current?.show({
      severity,
      summary,
      detail,
      life: 8000,
    });
  };

  const loadClassResults = async () => {
    try {
      if (!isLoading) setIsLoading(true);
      const schoolId = !empty(user) && !empty(user._id) ? user._id : "";

      const [
        studentsResult,
        classResult,
        termResult,
        sessionResult,
        subjectsResult,
        ratingsResult,
      ] = await Promise.all([
        studentApi.getStudentsInClass(
          schoolId,
          null,
          null,
          null,
          classId,
          "assessment",
        ),
        classApi.getSingleClass(classId, schoolId),
        termApi.getSingleTerm(schoolId, termId),
        sessionApi.getSession(sessionId, schoolId),
        classApi.getSubjectsInClass(classId, schoolId, 1000),
        assessmentApi.getRatings(schoolId),
      ]);

      const studentsResponse = prepareResponseData(studentsResult);
      setStudentsData(
        !empty(studentsResponse) &&
          studentsResponse.success &&
          isArray(studentsResponse.response)
          ? studentsResponse.response
          : [],
      );

      const classResponse = prepareResponseData(classResult);
      setClassData(
          classResponse?.success &&
          !isArray(classResponse.response)
          ? classResponse.response
          : {},
      );

      const termResponse = prepareResponseData(termResult);
      setTermData(
        !empty(termResponse) &&
          termResponse.success &&
          !empty(termResponse.response)
          ? termResponse.response
          : {},
      );

      const sessionResponse = prepareResponseData(sessionResult);
      setSessionData(
        !empty(sessionResponse) &&
          sessionResponse.success &&
          !empty(sessionResponse.response)
          ? sessionResponse.response
          : {},
      );

      const subjectsResponse = prepareResponseData(subjectsResult);
      setReIndexedSubjects(
        !empty(subjectsResponse) &&
          subjectsResponse.success &&
          !empty(subjectsResponse.response)
          ? reIndex(subjectsResponse.response, "_id")
          : {},
      );

      const ratingsResponse = prepareResponseData(ratingsResult);
      setRatingIndices(
        !empty(ratingsResponse) &&
          ratingsResponse.success &&
          !empty(ratingsResponse.response)
          ? ratingsResponse.response
          : [],
      );
    } catch (error) {
      responseDialog("error", "Error Alert!", errorResponse(error));
    } finally {
      setIsLoading(false);
    }
  };

  // Per-student report data, recomputed only when the underlying data
  // actually changes.
  const studentReports = useMemo(() => {
    if (empty(studentsData)) return [];
    return studentsData.map((student) =>
      buildStudentReport(
        student,
        classData,
        reIndexedSubjects,
        sessionId,
        termId,
      ),
    );
  }, [studentsData, classData, reIndexedSubjects, sessionId, termId]);

  // hide activation modal
  const hideActivationModal = () => {
    setActivationModalOpen(false);
  };

  // open activation modal for a specific student's report block
  const showActivationModal = (student) => {
    setStudentToActivate(student);
    setActivationModalOpen(true);
  };

  //function to activate student on payment
  const activateStudent = async () => {
    try {
      if (activationModalOpen) setActivationModalOpen(false);
      if (!isActionLoading) setIsActionLoading(true);
      const schoolId = !empty(user) && !empty(user._id) ? user._id : "";
      const studentId =
        !empty(studentToActivate) && !empty(studentToActivate._id)
          ? studentToActivate._id
          : "";
      const response = await studentApi.activateStudent([studentId], schoolId);
      const response_data = prepareResponseData(response);
      if (empty(response_data) || !response_data.success) {
        const error_response =
          !empty(response_data) && !empty(response_data.response)
            ? response_data.response
            : `Failed to activate student`;
        return responseDialog("error", "Error Alert", error_response);
      }

      responseDialog("success", "Success", `Activated student successfully!`);
      return loadClassResults();
    } catch (error) {
      responseDialog("error", "Error Alert!", errorResponse(error));
    } finally {
      setIsActionLoading(false);
    }
  };

  const activateStudentDialogFooter = (
    <div
      style={{
        marginTop: 20,
      }}
    >
      <Button
        label="No"
        icon="pi pi-times"
        style={{
          backgroundColor: "transparent",
          color: colors.danger,
          borderColor: colors.danger,
          borderWidth: 1,
        }}
        onClick={hideActivationModal}
      />
      <Button
        label="Yes"
        icon="pi pi-check"
        style={{
          backgroundColor: "transparent",
          color: colors.success,
          borderColor: colors.success,
          borderWidth: 1,
        }}
        onClick={() => {
          activateStudent();
        }}
      />
    </div>
  );
  const printReport = () => {
    window.print();
  };

  return (
    <>
      <AppWrapper {...props}>
        <main>
          <div className="tableCard">
            {/*  header start */}
            <MainHeader title="Result Sheet Display" />
            {/* end of header */}

            <div className="mt-10 mb-40">
              <div className="flex report-academic-details">
                <TitleValue
                  value={sessionData?.title || ""}
                  title="Selected Session:"
                />
                <TitleValue
                  value={termData?.title || ""}
                  title="Selected Term:"
                  mr="mr-30"
                />
                {!location?.state?.graduated && (
                  <TitleValue
                    value={classData?.title}
                    title="Selected Class:"
                    mr="mr-30"
                  />
                )}
                {location?.state?.graduated && (
                  <TitleValue
                    value="Graduated"
                    title="Student's Status:"
                    mr="mr-30"
                  />
                )}
              </div>
            </div>
            <div className="display-report">
              <div className="report-sheet-display" ref={printRef}>
                {!isLoading ? (
                  !empty(studentReports) ? (
                    studentReports.map((entry, index) => (
                      <div
                        key={`${index}${entry?.student._id || ''}`}
                        className="report-body-wrapper"
                        style={
                          index < studentReports.length - 1
                            ? { breakAfter: "page" }
                            : undefined
                        }
                      >
                        {entry.hasPaid ? (
                          <div className="report-body">
                            {/* report header */}
                            <div className="report-header">
                              <div style={{ width: 100, height: 100 }}>
                                <img
                                  src={
                                    !empty(user) && !empty(user.avatar)
                                      ? user.avatar
                                      : ""
                                  }
                                  alt="non"
                                  style={{ width: "100%", height: "100%" }}
                                />
                              </div>
                              <div>
                                <p className="report-title">
                                  {!empty(user) && !empty(user.name)
                                    ? user.name
                                    : "N/A"}
                                </p>
                                <p className="report-subtitle">
                                  {!empty(user) && !empty(user.address)
                                    ? user.address
                                    : "N/A"}
                                </p>
                                <p className="report-subtitle fs-14">
                                  {`TEL.: ${
                                    !empty(user) && !empty(user.phoneNumber1)
                                      ? user.phoneNumber1
                                      : ""
                                  }${
                                    !empty(user) && !empty(user.phoneNumber2)
                                      ? ", " + user.phoneNumber2
                                      : ""
                                  }${
                                    !empty(user) && !empty(user.ownerPhone)
                                      ? ", " + user.ownerPhone
                                      : ""
                                  }`}
                                </p>
                                <p className="report-subtitle fs-14 mb-10">
                                  {`Email: ${
                                    !empty(user) && !empty(user.email)
                                      ? user.email
                                      : ""
                                  }`}
                                </p>
                              </div>
                              <div style={{ width: 100, height: 100 }}>
                                {!empty(entry.activeDetails) &&
                                !empty(entry.activeDetails.avatar) ? (
                                  <img
                                    src={entry.activeDetails.avatar}
                                    alt="student"
                                    style={{ width: "100%", height: "100%" }}
                                  />
                                ) : (
                                  <img
                                    src={
                                      !empty(user) && !empty(user.avatar)
                                        ? user.avatar
                                        : ""
                                    }
                                    alt="non"
                                    style={{ width: "100%", height: "100%" }}
                                  />
                                )}
                              </div>
                            </div>
                            {/* end of report header */}

                            {/* report top section */}
                            <div className="report-details-header">
                              <p className="report-term-title">
                                {!empty(sessionData) &&
                                !empty(sessionData.title)
                                  ? sessionData.title
                                  : ""}{" "}
                                {!empty(termData) &&
                                !empty(termData.title) &&
                                isString(termData.title)
                                  ? termData.title.toUpperCase()
                                  : ""}{" "}
                                TERM STUDENT'S PERFORMANCE SHEET
                              </p>
                              <div className="report-detail-group">
                                <p className="report-detail-group-item">
                                  <span className="report-detail-group-title">
                                    NAME:
                                  </span>
                                  <span className="report-detail-group-value">
                                    <NavLink
                                      to={`/student/${entry.student?._id}/profile`}
                                      style={{
                                        textDecoration: "none",
                                        color: "#000000",
                                      }}
                                    >
                                      {!empty(entry.activeDetails) &&
                                      !empty(entry.activeDetails.fullName)
                                        ? toUpper(entry.activeDetails.fullName)
                                        : ""}
                                    </NavLink>
                                  </span>
                                </p>
                                <p className="report-detail-group-item">
                                  <span className="report-detail-group-title">
                                    GENDER:
                                  </span>
                                  <span className="report-detail-group-value">
                                    {!empty(entry.activeDetails) &&
                                    !empty(entry.activeDetails.gender)
                                      ? toUpper(entry.activeDetails.gender)
                                      : "N/A"}
                                  </span>
                                </p>
                                <p className="report-detail-group-item">
                                  <span className="report-detail-group-title">
                                    CLASS:
                                  </span>
                                  <span className="report-detail-group-value">
                                    {!empty(classData) &&
                                    !empty(classData.title)
                                      ? toUpper(classData.title)
                                      : ""}
                                  </span>
                                </p>
                              </div>
                              <div className="report-detail-group">
                                <p className="report-detail-group-item">
                                  <span className="report-detail-group-title">
                                    ADMISSION NO.:
                                  </span>
                                  <span className="report-detail-group-value">
                                    {!empty(entry.activeDetails) &&
                                    !empty(entry.activeDetails.regNo)
                                      ? toUpper(entry.activeDetails.regNo)
                                      : ""}
                                  </span>
                                </p>
                                <p className="report-detail-group-item">
                                  <span className="report-detail-group-title">
                                    ROLL NO.:
                                  </span>
                                  <span className="report-detail-group-value">
                                    {!empty(entry.activeDetails) &&
                                    !empty(entry.activeDetails.rollNumber)
                                      ? toUpper(entry.activeDetails.rollNumber)
                                      : "N/A"}
                                  </span>
                                </p>
                              </div>

                              <div className="report-rating-box">
                                <table className="grade-rating-table">
                                  <thead>
                                    <tr>
                                      <th>GRADE</th>
                                      <th>A</th>
                                      <th>B</th>
                                      <th>C</th>
                                      <th>D</th>
                                      <th>E</th>
                                      <th>F</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    <tr>
                                      <th>NO</th>
                                      <td>
                                        {!empty(entry.studentReport) &&
                                        !empty(entry.studentReport.noOfAs)
                                          ? toUpper(entry.studentReport.noOfAs)
                                          : 0}
                                      </td>
                                      <td>
                                        {!empty(entry.studentReport) &&
                                        !empty(entry.studentReport.noOfBs)
                                          ? toUpper(entry.studentReport.noOfBs)
                                          : 0}
                                      </td>
                                      <td>
                                        {!empty(entry.studentReport) &&
                                        !empty(entry.studentReport.noOfCs)
                                          ? toUpper(entry.studentReport.noOfCs)
                                          : 0}
                                      </td>
                                      <td>
                                        {!empty(entry.studentReport) &&
                                        !empty(entry.studentReport.noOfDs)
                                          ? toUpper(entry.studentReport.noOfDs)
                                          : 0}
                                      </td>
                                      <td>
                                        {!empty(entry.studentReport) &&
                                        !empty(entry.studentReport.noOfEs)
                                          ? toUpper(entry.studentReport.noOfEs)
                                          : 0}
                                      </td>
                                      <td>
                                        {!empty(entry.studentReport) &&
                                        !empty(entry.studentReport.noOfFs)
                                          ? toUpper(entry.studentReport.noOfFs)
                                          : 0}
                                      </td>
                                    </tr>
                                    <tr>
                                      <th colSpan={6}>
                                        NUMBER OF SUBJECTS OFFERED
                                      </th>
                                      <td>
                                        {entry.studentAssessment?.length || 0}{" "}
                                        out of
                                        {" " +
                                          entry.studentReport
                                            ?.noOfSubjectsOffered || 0}
                                      </td>
                                    </tr>
                                  </tbody>
                                </table>

                                <table className="score-rating-table">
                                  <tbody>
                                    <tr>
                                      <th>AVERAGE</th>
                                      <td>
                                        {!empty(entry.studentReport) &&
                                        !empty(entry.studentReport.average)
                                          ? entry.studentReport.average
                                          : "NA"}
                                      </td>
                                    </tr>
                                    <tr>
                                      <th>GRADE</th>
                                      <td>
                                        {
                                        !empty(entry.studentReport?.grade) &&
                                        !empty(entry.studentReport?.remark)
                                          ? entry.studentReport.grade +
                                            " - " +
                                            toUpper(entry.studentReport.remark)
                                          : "NA"}
                                      </td>
                                    </tr> 
                                    <tr>
                                      <th>POSITION</th>
                                      <td>
                                        {classData?.isStream === true ? toUpper(
                                              entry?.studentReport?.streamPosition || 'N/A') : toUpper(
                                              entry.studentReport.position || 'N/A'
                                            )}
                                      </td>
                                    </tr>
                                  </tbody>
                                </table>

                                <table className="score-rating-table">
                                  <tbody>
                                    <tr>
                                      <td>Number of Students in class</td>
                                      <td>
                                        {!empty(entry.activeDetails) &&
                                        !empty(
                                          entry.activeDetails.numberOfStudents,
                                        )
                                          ? entry.activeDetails.numberOfStudents
                                          : "NA"}
                                      </td>
                                    </tr>
                                    <tr>
                                      <td>Marks Obtained</td>
                                      <td>
                                        {!empty(entry.studentReport) &&
                                        !empty(
                                          entry.studentReport.cumulativeScore,
                                        )
                                          ? entry.studentReport.cumulativeScore
                                          : "NA"}
                                      </td>
                                    </tr>
                                    <tr>
                                      <td>Marks Obtainable</td>
                                      <td>
                                        {!empty(entry.studentReport) &&
                                        !empty(
                                          entry.studentReport
                                            .noOfSubjectsOffered,
                                        )
                                          ? entry.studentReport
                                              .noOfSubjectsOffered * 100
                                          : "NA"}
                                      </td>
                                    </tr>
                                  </tbody>
                                </table>
                              </div>
                            </div>
                            {/* end of report top section */}

                            <div className="report-containter">
                              <table className="report-sheet-area">
                                <thead>
                                  <tr>
                                    <th rowSpan={3}>COGNITIVE DOMAIN</th>
                                    <th rowSpan={4}>
                                      <span className="report-title-rotate">
                                        CODE
                                      </span>
                                    </th>
                                    <th rowSpan={2} colSpan={user?.noOfCAs  || 2}>
                                      CA
                                    </th>
                                    <th rowSpan={3}>
                                      <span className="report-title-rotate">
                                        EXAM
                                      </span>
                                    </th>
                                    <th rowSpan={3}>
                                      <span className="report-title-rotate">
                                        TOTAL
                                      </span>
                                    </th>
                                    <th rowSpan={4}>
                                      <span className="report-title-rotate">
                                        GRADE
                                      </span>
                                    </th>
                                    <th rowSpan={4}>
                                      <span className="report-title-rotate">
                                        POSITION
                                      </span>
                                    </th>
                                    <th rowSpan={4}>
                                      <span className="report-title-rotate">
                                        REMARKS
                                      </span>
                                    </th>
                                    <th rowSpan={4}>
                                      <span className="report-title-rotate">
                                        CLASS MIN.
                                      </span>
                                    </th>
                                    <th rowSpan={4}>
                                      <span className="report-title-rotate">
                                        CLASS MAX.
                                      </span>
                                    </th>
                                    <th rowSpan={4}>
                                      <span className="report-title-rotate">
                                        CLASS AVG.
                                      </span>
                                    </th>
                                  </tr>
                                  <tr></tr>
                                  <tr>
                                    {
                                      Array.from({ length: user?.noOfCAs || 2 }, (_, idx) => (
                                        <th key={idx}>{GetPosition(idx + 1)}</th>
                                      ))
                                    }
                                  </tr>
                                  <tr>
                                    <th>SUBJECTS</th>
                                    {
                                      Array.from({ length: user?.noOfCAs || 2 }, (_, idx) => (
                                        <th key={idx}>{user?.assessmentMaxScores?.[`ca${idx + 1}_max`] || '--'}</th>
                                      ))
                                    }
                                    <th>{user?.assessmentMaxScores?.exam_max || '--'}</th>
                                    <th>100</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {!empty(entry.studentAssessment) ? (
                                    entry.studentAssessment.map((data, idxx) => {
                                      return (
                                        <tr
                                          key={`${idxx}-${data?.subjectId || data?.subjectCode || ''}`}
                                        >
                                          <td>
                                            {!empty(data) &&
                                            !empty(data.subjectTitle)
                                              ? toUpper(data.subjectTitle)
                                              : ""}
                                          </td>
                                          <td>
                                            {!empty(data) &&
                                            !empty(data.subjectCode)
                                              ? data.subjectCode
                                              : 0}
                                          </td>
                                          {
                                            Array.from({ length: user?.noOfCAs || 2 }, (_, index) => (
                                              <td>
                                                { data?.[`ca${index + 1}`] || 0 }
                                              </td>
                                            ))
                                          }
                                          <td>
                                            {!empty(data) && !empty(data.exam)
                                              ? data.exam
                                              : 0}
                                          </td>
                                          <td>
                                            {!empty(data) && !empty(data.total)
                                              ? data.total
                                              : 0}
                                          </td>
                                          <td>
                                            {!empty(data) && !empty(data.grade)
                                              ? data.grade
                                              : ""}
                                          </td>
                                          <td>
                                            {classData?.isStream === true ? data?.streamPosition || 'N/A': data?.position || 'N/A'}
                                          </td>
                                          <td>
                                            {!empty(data) && !empty(data.remark)
                                              ? toUpper(data.remark)
                                              : ""}
                                          </td>
                                          <td>
                                            {!empty(data) &&
                                            !isUndefined(data.classMinValue)
                                              ? data.classMinValue
                                              : ""}
                                          </td>
                                          <td>
                                            {!empty(data) &&
                                            !isUndefined(data.classMaxValue)
                                              ? data.classMaxValue
                                              : ""}
                                          </td>
                                          <td>
                                            {!empty(data) &&
                                            !isUndefined(data.classAverage)
                                              ? data.classAverage
                                              : ""}
                                          </td>
                                        </tr>
                                      );
                                    })
                                  ) : (
                                    <tr></tr>
                                  )}
                                </tbody>
                              </table>
                            </div>
                            <div className="psycho_area">
                              {!empty(entry.activeDetails) &&
                              !empty(entry.activeDetails.psychomotor) ? (
                                <div className="pyschomotor_box">
                                  <table className="psycho-table">
                                    <thead>
                                      <tr>
                                        <th>Psychomotor</th>
                                        <th>Rating</th>
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {isObject(
                                        entry.activeDetails.psychomotor,
                                      ) &&
                                        Object.entries(
                                          entry.activeDetails.psychomotor,
                                        ).map(([key, value]) => (
                                          <tr key={key}>
                                            <td>
                                              {!empty(key)
                                                ? toNormalCase(key)
                                                : ""}
                                            </td>
                                            <td>
                                              {!empty(value) ? value : ""}
                                            </td>
                                          </tr>
                                        ))}
                                    </tbody>
                                  </table>
                                </div>
                              ) : null}
                              {!empty(entry.activeDetails) &&
                              !empty(entry.activeDetails.affectiveDomain) ? (
                                <div className="affective_domain_box">
                                  <table className="psycho-table">
                                    <thead>
                                      <tr>
                                        <th>Affective Domain</th>
                                        <th>Rating</th>
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {isObject(
                                        entry.activeDetails.affectiveDomain,
                                      ) &&
                                        Object.entries(
                                          entry.activeDetails.affectiveDomain,
                                        ).map(([key, value]) => (
                                          <tr key={key}>
                                            <td>
                                              {!empty(key)
                                                ? toNormalCase(key)
                                                : ""}
                                            </td>
                                            <td>
                                              {!empty(value) ? value : ""}
                                            </td>
                                          </tr>
                                        ))}
                                    </tbody>
                                  </table>
                                </div>
                              ) : null}
                              {!empty(entry.activeDetails) &&
                              (!empty(entry.activeDetails.affectiveDomain) ||
                                !empty(entry.activeDetails.psychomotor)) &&
                              !empty(ratingIndices) &&
                              isArray(ratingIndices) ? (
                                <div className="mt-10 rating_indicators">
                                  <strong>Rating Indicators</strong>
                                  <ul style={{ listStyle: "none" }}>
                                    {ratingIndices.map((item, index) => {
                                      const value =
                                        !empty(item) && !empty(item.value)
                                          ? item.value
                                          : "";
                                      const description =
                                        !empty(item) && !empty(item.description)
                                          ? item.description
                                          : "";
                                      return (
                                        <li className="mt-5" key={index}>
                                          <strong>{value}</strong> -{" "}
                                          {description}
                                        </li>
                                      );
                                    })}
                                  </ul>
                                </div>
                              ) : null}
                              {!empty(entry.activeDetails) &&
                              (!empty(entry.activeDetails.affectiveDomain) ||
                                !empty(entry.activeDetails.psychomotor)) &&
                              !empty(ratingIndices) &&
                              isArray(ratingIndices) ? (
                                <div
                                  style={{
                                    width: 400,
                                    height: 150,
                                    display: "flex",
                                    justifyContent: "center",
                                    marginBottom: 10,
                                  }}
                                >
                                  <Barchart data={entry.chartData} />
                                </div>
                              ) : (
                                <div
                                  style={{
                                    width: 500,
                                    height: 220,
                                    display: "flex",
                                    justifyContent: "center",
                                    marginBottom: 10,
                                  }}
                                >
                                  <Barchart data={entry.chartData} />
                                </div>
                              )}
                            </div>

                            <div className="report-summary-sheet">
                              <table className="report-summary-table">
                                <tbody>
                                  <tr>
                                    <td>
                                      <strong>Remarks from teacher</strong>
                                    </td>
                                    <td colSpan={3}>
                                      {!empty(entry.studentReport) &&
                                      !empty(entry.studentReport.comment)
                                        ? entry.studentReport.comment
                                        : !empty(entry.studentReport) &&
                                            !empty(
                                              entry.studentReport
                                                .teachersComment,
                                            )
                                          ? entry.studentReport.teachersComment
                                          : ""}
                                    </td>
                                  </tr>
                                  <tr>
                                    <td>Teacher's Name</td>
                                    <td colSpan={3}>
                                      {entry.classTeacherName}
                                    </td>
                                  </tr>
                                  <tr>
                                    <td>Principal's Remark</td>
                                    <td colSpan={3}>
                                      {!empty(entry.studentReport) &&
                                      !empty(
                                        entry.studentReport.principalComments,
                                      )
                                        ? entry.studentReport.principalComments
                                        : "NA"}
                                    </td>
                                  </tr>
                                  <tr>
                                    <td>Director's Name</td>
                                    <td>Mr. Ukoha Orji Okoro</td>
                                    <td colSpan={2}>
                                      {user?.signature ? (
                                        <div className="flex col-g-8">
                                          <span>Sign:</span>
                                          <img
                                            src={user?.signature || ""}
                                            alt="signature"
                                            style={{
                                              width: 40,
                                              height: "auto",
                                            }}
                                          />
                                        </div>
                                      ) : (
                                        <div>
                                          Sign: <em>Management</em>
                                        </div>
                                      )}
                                    </td>
                                  </tr>
                                  <tr>
                                    <td>Next Term Begins</td>
                                    <td>
                                      {!empty(user) && !empty(user.openingDate)
                                        ? user.openingDate
                                        : "N/A"}
                                    </td>
                                    <td>Closing Date: </td>
                                    <td>
                                      {!empty(user) && !empty(user.closingDate)
                                        ? user.closingDate
                                        : "N/A"}
                                    </td>
                                  </tr>
                                </tbody>
                              </table>
                            </div>
                          </div>
                        ) : (
                          <div className="mt-50 assessment_not_paid">
                            <FaExclamationTriangle color="red" size={25} />
                            <p className="mt-10 fs-18 mb-5">
                              <strong>
                                {!empty(entry.activeDetails) &&
                                !empty(entry.activeDetails.fullName)
                                  ? entry.activeDetails.fullName
                                  : "Result Sheet Unavailable"}
                              </strong>
                            </p>
                            <p>This student has an outstanding portal levy</p>
                            <div>
                              <ButtonIcon
                                height={45}
                                color={colors.primary}
                                backgroundColor="transparent"
                                width={220}
                                fontWeight="bold"
                                borderColor="transparent"
                                buttonText="Activate Student"
                                onClick={() =>
                                  showActivationModal(entry.student)
                                }
                              />
                            </div>
                          </div>
                        )}
                      </div>
                    ))
                  ) : (
                    <div className="flex justify-center w-100pc">
                      <EmptyListResponse message="There are no results available for this class this term." />
                    </div>
                  )
                ) : (
                  <ItemLoader />
                )}
              </div>
            </div>
            {!isLoading && !empty(studentReports) && (
              <div className="flex flex-center mt-20">
                <ButtonIcon
                  height={45}
                  marginTop={2}
                  color={colors.primary}
                  backgroundColor={colors.white}
                  width={180}
                  borderColor={colors.primary}
                  buttonText={
                    <div>
                      <FaPrint /> Print Results
                    </div>
                  }
                  type="submit"
                  borderHover={false}
                  onClick={() => printReport()}
                />
              </div>
            )}
          </div>
        </main>
        {isActionLoading && <FullPageLoader visible={isActionLoading} />}
      </AppWrapper>

      <Dialog
        visible={activationModalOpen}
        style={{ width: "32rem", zIndex: 999999 }}
        breakpoints={{ "960px": "75vw", "641px": "90vw" }}
        header="Confirm"
        modal
        footer={activateStudentDialogFooter}
        onHide={hideActivationModal}
      >
        <div
          className="confirmation-content"
          style={{ marginTop: 20, display: "flex", alignItems: "center" }}
        >
          <i
            className="pi pi-exclamation-triangle mr-3"
            style={{ fontSize: "2rem", marginRight: 15, color: "#e65061" }}
          />
          {
            <>
              <p>
                Are you sure you want to <strong>activate</strong> this student?{" "}
                <br />
                <br />
                By clicking "Yes", you are confirming that{" "}
                <strong>
                  {!empty(studentToActivate) &&
                  !empty(studentToActivate.fullName)
                    ? studentToActivate.fullName
                    : ""}
                </strong>{" "}
                made portal payment for this term.
                <br />
                <br />
                <em style={{ color: colors.danger }}>
                  <strong>Note:</strong> This action cannot be undone.
                </em>
              </p>
            </>
          }
        </div>
      </Dialog>
      <Toast ref={toastTR} style={{ zIndex: 99999 }} position="bottom-left" />
    </>
  );
};

export default DisplayAllClassResults;
